'use es6';

import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { mergePriceObjects } from 'pricing-pages-lib/utils/pricingUtils';
import { useSelector } from 'react-redux';
import { QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import styled from 'styled-components';
import { activeCurrencySelector } from '../../selectors/CurrencySelectors';
import { priceBreakdownSelector } from '../../selectors/PricingSelectors';
import { productsFromProductInventoryQuantitySelector } from '../../selectors/ProductInventorySelectors';
import { crmSuiteProductLevelSelector, productVersionSelector, skuIdIsAddonOrServiceSelector, skuIdIsPartOfCrmSuiteSelector, skuSelector } from '../../selectors/ProductsSelectors';
import PriceDisplay from '../common/pricing/PriceDisplay';
import Divider from './Divider';
import ShoppingCartItem from './ShoppingCartItem';
const HeaderWrapper = styled.div.withConfig({
  displayName: "ShoppingCart__HeaderWrapper",
  componentId: "sc-6pi3zk-0"
})(["font-weight:600;margin-bottom:10px;"]);

const shouldIncludeLineItemSelector = (state, {
  productConfigurations,
  skuId,
  quantity
}) => {
  const {
    skuType,
    productVersionSkuId
  } = skuSelector(state, {
    skuId
  });
  const ownedQuantity = productsFromProductInventoryQuantitySelector(state, {
    skuId
  });
  const isAddonOrService = skuIdIsAddonOrServiceSelector(state, {
    skuId
  });
  const {
    quantifiable
  } = productVersionSelector(state, {
    skuId
  });
  const isAddingZeroUnits = (isAddonOrService && quantifiable || skuType === QUANTITY_PACK) && ownedQuantity === quantity;
  const shouldLumpIntoHubLineItem = skuType === QUANTITY_PACK && productConfigurations.some(({
    skuId: searchSkuId
  }) => searchSkuId === productVersionSkuId);
  return !(isAddingZeroUnits || shouldLumpIntoHubLineItem);
};

const ShoppingCart = ({
  productConfigurations,
  onRemove,
  className,
  requiredSkuIds,
  queryParamTerm
}) => {
  const suiteProductLevel = useSelector(state => crmSuiteProductLevelSelector(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  }));
  const [suiteProductConfigs, nonSuiteProductConfigs] = useSelector(state => productConfigurations.reduce((acc, config) => {
    const productVersion = productVersionSelector(state, {
      skuId: config.skuId
    });
    const isPartOfSrmSuite = skuIdIsPartOfCrmSuiteSelector(state, {
      skuId: productVersion.skuId,
      suiteProductLevel
    });
    if (isPartOfSrmSuite) return [[...acc[0], config], acc[1]];
    return [acc[0], [...acc[1], config]];
  }, [[], []]));
  const [filteredSuiteProductConfigs, filteredNonSuiteProductConfigs] = useSelector(state => [suiteProductConfigs.filter(config => shouldIncludeLineItemSelector(state, Object.assign({
    productConfigurations
  }, config))), nonSuiteProductConfigs.filter(config => shouldIncludeLineItemSelector(state, Object.assign({
    productConfigurations
  }, config)))]);
  const filteredProductConfigurations = useSelector(state => productConfigurations.filter(({
    skuId,
    quantity
  }) => shouldIncludeLineItemSelector(state, {
    skuId,
    quantity,
    productConfigurations
  })));
  const filteredSkuIds = filteredProductConfigurations.map(({
    skuId
  }) => skuId);
  const [filteredSuiteSkuIds, filteredNonSuiteSkuIds] = [filteredSuiteProductConfigs.map(({
    skuId
  }) => skuId), filteredNonSuiteProductConfigs.map(({
    skuId
  }) => skuId)];
  let content;

  const makeShoppingCartItem = (skuId, priceOverride) => /*#__PURE__*/_jsx(ShoppingCartItem, {
    queryParamTerm: queryParamTerm,
    onRemove: () => onRemove(skuId),
    skuId: skuId,
    allProductConfigurations: productConfigurations,
    isCloseable: !requiredSkuIds.includes(skuId),
    shouldDisplayPrice: !(suiteProductLevel && filteredSuiteSkuIds.includes(skuId)),
    priceOverride: priceOverride
  }, skuId);

  const currencyCode = useSelector(activeCurrencySelector);
  const priceBreakdown = useSelector(state => {
    return priceBreakdownSelector(state, {
      productConfigurations,
      queryParamTerm
    });
  });
  const suiteItemsPrice = mergePriceObjects(...priceBreakdown.filter(({
    skuId
  }) => suiteProductConfigs.some(({
    skuId: searchSkuId
  }) => searchSkuId === skuId)));
  const nonSuiteItemsPrice = mergePriceObjects(...priceBreakdown.filter(({
    skuId
  }) => nonSuiteProductConfigs.some(({
    skuId: searchSkuId
  }) => searchSkuId === skuId)));

  if (suiteProductLevel) {
    const suiteItems = filteredSuiteSkuIds.map(skuId => makeShoppingCartItem(skuId));
    const nonSuiteItems = filteredNonSuiteSkuIds.map(skuId => makeShoppingCartItem(skuId, priceBreakdown.find(({
      skuId: searchSkuId
    }) => skuId === searchSkuId)));
    content = /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(HeaderWrapper, {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "pricingPage.planBuilder.shoppingCart.costOnlySuiteCrmSuite_jsx",
          options: {
            priceDisplayProps: {
              price: suiteItemsPrice.monthly.calculatedPrice,
              currencyCode
            }
          },
          elements: {
            PriceDisplay
          }
        })
      }), suiteItems, Boolean(nonSuiteItems.length) && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsx(HeaderWrapper, {
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "pricingPage.planBuilder.shoppingCart.costAddOnsAndServices_jsx",
            options: {
              priceDisplayProps: {
                price: nonSuiteItemsPrice.monthly.calculatedPrice,
                currencyCode
              }
            },
            elements: {
              PriceDisplay
            }
          })
        }), nonSuiteItems]
      })]
    });
  } else {
    const items = filteredSkuIds.map(skuId => makeShoppingCartItem(skuId));
    if (items.length === 0) return null;
    content = /*#__PURE__*/_jsx("div", {
      children: items
    });
  }

  return /*#__PURE__*/_jsxs("div", {
    className: className,
    children: [/*#__PURE__*/_jsx(Divider, {
      className: "m-top-0"
    }), content, /*#__PURE__*/_jsx(Divider, {
      className: "m-bottom-0"
    })]
  });
};

export default ShoppingCart;